import { useState } from 'react';

import Head from 'next/head';
import dynamic from 'next/dynamic';

import { StaticPageProps } from 'ssr/getStaticPageProps';

import { fontClassNames } from 'font';

import RouteProgress from 'libs/router/RouteProgress';
import { subscribeStaticContentLoading } from 'libs/router/staticContentLoadingSignal';
import { withDomainSuffix } from 'libs/seo';

import { UserCountryProvider } from 'context/UserCountryContext';
import { LeadProvider } from 'context/LeadContext';
import { ConsentProvider } from 'context/ConsentContext';
import { ContentProvider, initialValues as initialContentValues } from 'context/ContentContext';

import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';

import { DynamicImport } from 'ui/DynamicImport';

import { FooterLoader } from 'features/layout/organisms/Footer/FooterLoader/FooterLoader';
import { Layout, LayoutContainer, LayoutContent } from 'features/layout/organisms/Layout';
import Header from 'features/layout/organisms/Header/Header';

import { staticPages } from './registry';

type Props = { slug: string } & StaticPageProps;

/**
 * Renders one entry from src/landing-pages/registry.tsx: header,
 * footer, and providers (same chrome the CMS-driven Page.tsx builds, minus
 * its "page" record fetch and page_type dispatch), wrapping the page's own
 * Component. `internationalization` (populated only when the registry entry
 * has an `internationalizationKey`) flows straight through ContentContext,
 * so section components can call useInt() exactly like CMS-driven pages do.
 *
 * pages/[...route].tsx is the only caller — it renders this instead of the
 * CMS flow whenever the requested slug exists in the registry.
 */
export const StaticPageRoute = ({ slug, ...navProps }: Props) => {
  const { Component, title, description } = staticPages[slug];

  // The <Head> renders outside the ContentProvider below, so useInt() is unavailable here. Resolve
  // the SEO title/description straight from the fetched dictionary + locale instead, so it/es pages
  // ship translated <title>/<meta> rather than the English registry defaults. Falls back to English.
  const localizeMeta = (value: string): string => {
    const entry = navProps.internationalization?.[value] as string | Record<string, string> | undefined;

    if (!entry) return value;
    if (typeof entry === 'string') return entry;

    return entry[navProps.lang] || entry.en || value;
  };

  // Registry titles/descriptions are stored bare; the `- globaldatabase.com` brand suffix is appended here.
  const localizedTitle = withDomainSuffix(localizeMeta(title));
  const localizedDescription = withDomainSuffix(localizeMeta(description));

  // hreflang alternates come from navProps.links (one localized URL per language);
  // x-default points at the English URL.
  const xDefault = navProps.links?.find((i) => i.key === 'en')?.link;

  // Must default to false: useLayoutEffect never runs on the server, so
  // starting true would ship the page hidden in the actual SSR'd HTML (bad
  // for SEO/no-JS). On a client-side nav where the chunk isn't ready yet,
  // StaticPageLoading's own layout effect (a child, so it fires first in the
  // same commit) flips the shared signal to true before this component's
  // effect below even subscribes — subscribe() replays the current value
  // synchronously, and being a layout effect, the resulting re-render is
  // flushed before the browser paints, so there's still no visible flash.
  const [isContentLoading, setIsContentLoading] = useState(false);

  useIsomorphicLayoutEffect(() => subscribeStaticContentLoading(setIsContentLoading), []);

  return (
    <>
      <RouteProgress />

      <Head>
        <meta name="viewport" content="width=device-width" />
        <title>{localizedTitle}</title>
        <meta name="description" content={localizedDescription} />
        <meta property="og:title" content={localizedTitle} />
        <meta property="og:description" content={localizedDescription} />

        {/* canonical is emitted globally from asPath in pages/_app.tsx */}
        {navProps.links?.map((i) => (
          <link key={i.key} rel="alternate" hrefLang={i.key} href={`${process.env.HOST || ''}${i.link || ''}`} />
        ))}
        {xDefault && <link rel="alternate" hrefLang="x-default" href={`${process.env.HOST || ''}${xDefault}`} />}
      </Head>

      <main
        data-page-type="ssr"
        className={fontClassNames}
        style={{
          opacity: isContentLoading ? 0 : 1,
          pointerEvents: isContentLoading ? 'none' : 'auto',
          transition: 'opacity 150ms ease',
        }}
      >
        <UserCountryProvider>
          <LeadProvider>
            <ConsentProvider>
              <ContentProvider
                {...initialContentValues}
                {...navProps}
                data={{} as (typeof initialContentValues)['data']}
                page={1}
                pathname=""
                additionals={{} as (typeof initialContentValues)['additionals']}
                clientNavigationGroups={{}}
              >
                <Layout>
                  <LayoutContainer>
                    <Header />

                    <LayoutContent>
                      <Component />
                    </LayoutContent>
                  </LayoutContainer>

                  <DynamicImport fallback={() => <FooterLoader />}>
                    {() => {
                      const FooterComponent = dynamic(() => import('features/layout/organisms/Footer/Footer'), {
                        ssr: false,
                        loading: () => <FooterLoader />,
                      });

                      return <FooterComponent hasBookDemo />;
                    }}
                  </DynamicImport>
                </Layout>
              </ContentProvider>
            </ConsentProvider>
          </LeadProvider>
        </UserCountryProvider>
      </main>
    </>
  );
};
